<template>
  <!--
    Plan 091 FIX B: single real root element. The previous template had two root
    nodes (the conditional <ChannelSection> + the teleported <OfferDetailModal>),
    so it rendered as a Vue FRAGMENT and the parent's `id="offers"` fell through
    as an extraneous attribute (warning) that attached to nothing — leaving the
    REWARDS top-bar tab's `#offers` scroll target unresolved. Wrapping everything
    in one <section> gives `id="offers"` a concrete element to land on (Vue
    auto-forwards the fallthrough `id` to this single root), so the anchor
    resolves and the warning is gone. The OfferDetailModal still teleports out of
    this subtree, so nesting it here changes no rendered DOM.
  -->
  <section :aria-label="$t('channel.offers.section.title')">
    <ChannelSection v-if="hasOffers || loading">
      <template #title>
        {{ $t("channel.offers.section.title") }}
      </template>

      <template #actions>
        <!--
          Plan 091 FIX #4: fan-visible carousel arrows. Shown in the new channel
          layout for every viewer (the Figma REWARDS header has two red circular
          ‹ › buttons), scrolling the full-bleed rail one page at a time. Hidden
          while loading and on the legacy path (DOM stays byte-for-byte
          unchanged). The host-only manage/+ dropdown is kept.
        -->
        <ChannelRailArrows
          v-if="newLayout && !loading && hasOffers"
          :rail="railRef"
        />

        <DropdownMenuRoot
          v-if="channelStore.permissions.manageOffers"
          v-model:open="dropdownOpen"
        >
          <DropdownMenuTrigger as-child>
            <Button
              type="button"
              variant="icon"
              class="btn-circle border-border-brand size-10"
              :aria-label="$t('channel.offers.section.dropdown.createOffer')"
            >
              <Icon
                class="text-text size-6"
                icon="ic:baseline-plus"
                aria-hidden="true"
              />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuPortal>
            <DropdownMenuContent
              class="outline-none shadow bg-surface-secondary/90 text-text-secondary backdrop-blur-3xl p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade min-w-60 z-50 font-sans text-sm"
              :side-offset="5"
            >
              <DropdownMenuItem
                class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand text-text data-[highlighted]:text-text-on-brand rounded-md px-2 py-1 gap-2 cursor-pointer"
                @click="onCreateOffer"
              >
                {{ $t("channel.offers.section.dropdown.createOffer") }}
              </DropdownMenuItem>

              <DropdownMenuItem
                class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand text-text data-[highlighted]:text-text-on-brand rounded-md px-2 py-1 gap-2 cursor-pointer"
                @click="onManageOffers"
              >
                {{ $t("channel.offers.section.dropdown.manageOffers") }}
              </DropdownMenuItem>

              <DropdownMenuArrow
                class="fill-surface-secondary/90 backdrop-blur-3xl"
              />
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenuRoot>
      </template>

      <div v-if="!loading" class="relative">
        <!--
          In the new channel layout the REWARDS rail is a
          full-bleed carousel that overflows to the page-column region edges
          while the section header stays centered. The breakout is applied ONLY
          in the new-layout path (FullBleedRail :enabled); with the flag off it
          renders a transparent passthrough so the legacy rail DOM is
          byte-for-byte unchanged.
        -->
        <FullBleedRail :enabled="newLayout">
          <ChannelRailVirtualized
            ref="railRef"
            :items="offers"
            :get-key="(offer) => offer.id"
            :item-width="newLayout ? 361 : 312"
          >
            <template #default="{ item: { data: offer } }">
              <OfferCard
                :offer="offer"
                @open-detail="onOfferClick(offer)"
                @deleted="onOfferDeleted"
              />
            </template>
          </ChannelRailVirtualized>
        </FullBleedRail>

        <!--
          Right-edge fade behind the "loading more" spinner. Uses the
          theme-adaptive page-background token `bg` (the surface the rail sits
          on) so the gradient is visible in both themes — the previous
          `from-surface-primary` / `via-surface-primary/80` were no-op classes
          (no `surface-primary` token exists), so the fade rendered transparent
          and the spinner floated over the cards with no backdrop.
        -->
        <div
          v-if="loadingMore"
          class="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-gradient-to-l from-bg via-bg/80 to-transparent pl-8 pr-5"
        >
          <AccessibleSpinner
            :label="$t('channel.offers.section.loadingMore')"
            size="sm"
          />
        </div>
      </div>

      <SkeletonRail v-else :count="3">
        <template #default="{ index }">
          <OfferCardSkeleton :index="index" />
        </template>
      </SkeletonRail>
    </ChannelSection>

    <OfferDetailModal
      v-model="isModalOpen"
      :offer="selectedOffer"
      @claimed="onOfferClaimed"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useInfiniteScroll } from "@vueuse/core";
import { useChannelStore } from "@/stores/channel.store";
import { RouteNames } from "@/router/app.router";
import ChannelSection from "@/components/channel/channel-section.vue";
import ChannelRailVirtualized from "@/components/channel/channel-rail-virtualized.vue";
import ChannelRailArrows from "@/components/channel/channel-rail-arrows.vue";
import FullBleedRail from "@/components/channel/full-bleed-rail.vue";
import { useFeatureFlags } from "@/composables/use-feature-flags";
import SkeletonRail from "@/components/ui/skeleton/skeleton-rail.vue";
import AccessibleSpinner from "@/components/ui/accessible-spinner.vue";
import OfferCard, { type OfferCardData } from "./offer-card.vue";
import OfferCardSkeleton from "./offer-card-skeleton.vue";
import OfferDetailModal, {
  type OfferDetailModalData,
} from "./offer-detail-modal.vue";
import { Icon } from "@iconify/vue";
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import Button from "@/components/ui/button/button.vue";

const props = withDefaults(
  defineProps<{
    offers: (OfferCardData & OfferDetailModalData)[];
    loading?: boolean;
    loadingMore?: boolean;
    /**
     * Whether more pages remain to load. Gates the infinite-scroll trigger so
     * an empty / short rail (no scrollable overflow) does not emit `loadMore`
     * every `interval`ms forever. Defaults to true so callers that omit it keep
     * the prior (ungated) behaviour.
     */
    hasMore?: boolean;
  }>(),
  { hasMore: true },
);

const emit = defineEmits<{
  loadMore: [];
  refetch: [];
}>();

const channelStore = useChannelStore();
const router = useRouter();
const dropdownOpen = ref(false);
const railRef = ref<HTMLElement | null>(null);

const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");

const hasOffers = computed(() => props.offers.length > 0);

useInfiniteScroll(
  railRef,
  () => {
    emit("loadMore");
  },
  {
    direction: "right" as const,
    distance: 100,
    interval: 300,
    canLoadMore: () => props.hasMore && !props.loadingMore,
  },
);

function onCreateOffer() {
  dropdownOpen.value = false;
  router.push({
    name: RouteNames.CreateOffer,
    params: { channel: channelStore.channelSlug },
  });
}

function onManageOffers() {
  dropdownOpen.value = false;
  router.push({
    name: RouteNames.ManageOffers,
    params: { channel: channelStore.channelSlug },
  });
}

const isModalOpen = ref(false);
const selectedOffer = ref<OfferDetailModalData | null>(null);

function onOfferClick(offer: OfferCardData & OfferDetailModalData) {
  selectedOffer.value = offer;
  isModalOpen.value = true;
}

function onOfferClaimed() {
  emit("refetch");
}

function onOfferDeleted() {
  emit("refetch");
}
</script>
